import { Link, NavLink, useLocation } from "react-router-dom";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/guide", label: "Guide" },
  { to: "/categorie", label: "Categorie" },
  { to: "/chi-sono", label: "Chi sono" },
  { to: "/contatti", label: "Contatti" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuId = useId();
  const openButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    // Restore focus to the open control after close animation frame
    requestAnimationFrame(() => openButtonRef.current?.focus());
  }, []);

  // Body scroll lock + Escape + focus first control when drawer opens
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeMenu]);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-warm-white border-b border-wood/30" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="page-container py-5 flex items-center justify-between">
          <Link
            to="/"
            className="text-stone2-dark"
            data-testid="nav-logo"
            aria-label="Nestly — Home"
          >
            <Logo wordmarkClassName="text-2xl md:text-3xl" />
          </Link>

          <nav className="hidden md:flex items-center gap-9" aria-label="Principale">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `link-under text-sm uppercase tracking-[0.18em] font-semibold transition-colors ${
                    isActive ? "text-sage-ink" : "text-stone2-dark hover:text-sage-ink"
                  }`
                }
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <button
            ref={openButtonRef}
            type="button"
            className="md:hidden inline-flex items-center justify-center min-h-11 min-w-11 text-stone2-dark -mr-2"
            onClick={() => setOpen(true)}
            aria-label="Apri menu"
            aria-expanded={open}
            aria-controls={menuId}
            data-testid="nav-mobile-open"
          >
            <Menu size={26} strokeWidth={1.4} aria-hidden="true" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id={menuId}
            role="dialog"
            aria-modal="true"
            aria-label="Menu di navigazione"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-warm-white md:hidden"
            data-testid="nav-mobile-panel"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo wordmarkClassName="text-2xl" />
              <button
                ref={closeButtonRef}
                type="button"
                onClick={closeMenu}
                aria-label="Chiudi menu"
                className="inline-flex items-center justify-center min-h-11 min-w-11 -mr-2"
                data-testid="nav-mobile-close"
              >
                <X size={26} strokeWidth={1.4} aria-hidden="true" />
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-10 gap-6" aria-label="Menu mobile">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.6 }}
                >
                  <Link
                    to={l.to}
                    className="font-display text-5xl leading-none"
                    onClick={closeMenu}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
